import { useLingui } from "@lingui/react/macro";

import { trackEvent } from "@/shared/analytics";
import { activateLocale, type Locale, LOCALES } from "@/shared/i18n";
import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import { useCurrentUser } from "../api/useCurrentUser";
import { useUpdateLocale } from "../api/useUpdateLocale";

// Название языка на нём самом («Русский», «English») — его не переводят.
const nativeName = (locale: Locale): string => {
  const name = new Intl.DisplayNames([locale], { type: "language" }).of(locale) ?? locale;
  return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
};

// Переключатель ru/en. Язык меняется сразу, без перезагрузки; вошедшему игроку он
// сохраняется в профиль (`PATCH /api/me`). Гостю — только до перезагрузки страницы.
export const LocaleSwitcher = ({ className }: { className?: string }) => {
  const { i18n, t } = useLingui();
  const { data: user } = useCurrentUser();
  const updateLocale = useUpdateLocale();

  const choose = async (locale: Locale) => {
    if (locale === i18n.locale) {
      return;
    }
    trackEvent("locale_switch");
    await activateLocale(locale);
    if (user) {
      updateLocale.mutate({ body: { locale } });
    }
  };

  return (
    <div
      role="group"
      aria-label={t`Interface language`}
      className={cn("flex gap-0.5 rounded-lg border border-border bg-background p-0.5", className)}
    >
      {LOCALES.map((locale) => (
        <Button
          key={locale}
          variant="ghost"
          size="sm"
          lang={locale}
          aria-label={nativeName(locale)}
          aria-pressed={locale === i18n.locale}
          className="uppercase aria-pressed:bg-surface-raised aria-pressed:font-semibold"
          onClick={() => {
            void choose(locale);
          }}
        >
          {locale}
        </Button>
      ))}
    </div>
  );
};
